import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from "vuex-persistedstate";
import _ from "lodash";
import Bugsnag from '@bugsnag/js';

// Modules
import showing from './modules/showing';

Vue.use(Vuex)

/**
 * Handle user session.
 *
 * These where originally on the root instance, but since we don't
 * have access to it from the store these are provided here instead.
 */

/**
 * prevent multiple alerts if user is signed out,
 * since validation could be called simultaneously.
 */
let counter = 0;
window.sessionTimer = 0;
setInterval(() => {
  window.sessionTimer = 0;
}, 300000);

window.storeSignout = () => {
  console.info("StoreSignout triggerd...");
  fetch('/user/logout/', {
    method: 'GET',
    mode: 'no-cors',
    credentials: 'include',
    redirect: 'manual'
  })
  .then(() => {
    // remove everything stored by persistant state
    localStorage.removeItem('vuex');
    window.location.href = '/login';
  });
}
/**
 * If the response from server is redirect to login we should do
 * same in app.
 * @param response
 */
window.storeValidateSession = (response) => {
  console.info("Store validate session running....");
  if (response.status === 401 && response.url.indexOf("auth/forbidden") !== -1 && counter < 1) {
    console.info("User session revoked. Signing out.");
    alert("Du har loggats ut pga inaktivitet.");
    window.storeSignout();
    counter++;
  }
}

export default new Vuex.Store({
  plugins: [createPersistedState()],
  state: {
    editPrio: null,
    editBid: null,
    loggedInUser: null,
    loggedInUserInfo: null,
    authenticated: false,
    listingsWithActiveBiddings: null,
    allRecentListings: [],
    currentListingWithBiddings: null,
    currentListingWithBiddingsBidReceivers: null,
    currentUser: {},
    currentUserMessagingTemplates: null,
    receivers: [],
    // to set default values for sendSmsOrMail modal
    sendSmsOrMailContactData: {
      chosenMessagingTemplate: "",
      message: "",
      receivers: [],
      subject: "",
      deliveryWay: ""
    },
    activeComponent: null,
    sendSmsOrMailWhichService: null,

  },
  /**
   * Mutations mutates the current state.
   * The mutations can be tracked in Vue devtools -> vuex
   *
   * Remember that mutations have to be synchronous.
   */
  mutations: {
    SET_EDIT_BID_AMOUNT(state, payload) {
      state.editBid.amount = payload;
    },
    SET_EDIT_BID_CONFIRMEDFORPUBLISH(state, payload) {
      state.editBid.confirmedForPublish = payload;
    },
    SET_EDIT_BID_COMMENT_INTERNAL(state, payload) {
      state.editBid.commentInternal = payload;
    },
    SET_EDIT_BID_COMMENT(state, payload) {
      state.editBid.comment = payload;
    },
    SET_EDIT_BID_DATE(state, payload) {
      state.editBid.date = payload;
    },
    SET_EDIT_BID_HIDE(state, payload) {
      state.editBid.hide = payload;
    },
    SET_CURRENT_LISTING_WITH_ACTIVE_BIDDINGS_BID_RECIEVERS(state, payload) {
      state.currentListingWithBiddingsBidReceivers = payload;
    },
    SET_EDIT_BID(state, payload) {
      state.editBid = payload;
    },
    SET_EDIT_PRIO(state, payload) {
      state.editPrio = payload;
    },
    SET_AUTHENTICATED(state, payload) {
      state.authenticated = true;
      state.loggedInUser = payload;
    },
    SET_AUTHENTICATED_USER_INFO(state, payload) {
      state.loggedInUserInfo = payload
    },
    SET_UNAUTHENTICATED(state) {
      state.authenticated = false;
    },
    STORE_LISTINGS_WITH_ACTIVE_BIDDINGS(state, payload) {
      state.listingsWithActiveBiddings = payload;
    },
    STORE_CURRENT_LISTING_WITH_ACTIVE_BIDDINGS(state, payload) {
      state.currentListingWithBiddings = payload;
    },
    STORE_CURRENT_USER_DATA(state, payload) {
      state.currentUser = payload;
    },
    STORE_CURRENT_USER_MESSAGING_TEMPLATES(state, payload) {
      state.currentUserMessagingTemplates = payload;
    },
    STORE_SEND_SMS_OR_MAIL_CONTACT_DATA(state, payload) {
      state.sendSmsOrMailContactData = payload;
    },
    STORE_SMS_MAIL_DELIVERYWAY(state, payload) {
      state.sendSmsOrMailContactData.deliveryWay = payload;
    },
    STORE_SMS_MAIL_SUBJECT(state, payload) {
      state.sendSmsOrMailContactData.subject = payload;
    },
    STORE_SMS_MAIL_CONTENT(state, payload) {
      state.sendSmsOrMailContactData.message = payload;
    },
    STORE_SMS_MAIL_RECIEVERS(state, payload) {
      state.sendSmsOrMailContactData.receivers = payload;
    },
    STORE_SMS_MAIL_CHOSEN_MESSAGING_TEMPLATE(state, payload){
      state.sendSmsOrMailContactData.chosenMessagingTemplate = payload;
    },
    // Bidding or Showing
    STORE_SMS_MAIL_WHICH_SERVICE(state, payload){
      state.sendSmsOrMailWhichService = payload;
    },
    STORE_ALL_RECENT_LISTINGS(state, payload) {
      state.allRecentListings = payload;
    },
    STORE_ACTIVE_COMPONENT(state, payload) {
      state.activeComponent = payload;
    },

  },
  /**
   * Actions are similar to mutations but instead of mutating state,
   * actions commit mutations and does not have to be synchronous.
   *
   * https://vuex.vuejs.org/guide/actions.html#dispatching-actions
   */
  actions: {
    /**
     * Fetch listings with active biddings, note: the fact that fetch returns
     * a promise is key here, otherwise computed property would
     * be undefined.
     * @param state
     */
    storeListingsWithActiveBiddings(state) {
      fetch(process.env.VUE_APP_API_URL + '/internalapi/object-list/active-bidding/', {
        method: 'GET',
        credentials: 'include'
      })
      .then(response => {
        window.storeValidateSession(response);
        return response.json()
      })
      .then((data) => {
        state.commit("STORE_LISTINGS_WITH_ACTIVE_BIDDINGS", data.data);
      })
      .catch((error) => {
        Bugsnag.notify(error);
      });
    },
    // Listings for "ny budgivning"
    storeAllRecentListings(state) {
      //placed on a new bidding object.
      fetch(process.env.VUE_APP_API_URL + '/internalapi/object-list/my-without-active-bidding', {
        method: 'GET',
        credentials: 'include'
      })
      .then(response => {
        window.storeValidateSession(response);
        return response.json()
      })
      .then((data) => {
        state.commit("STORE_ALL_RECENT_LISTINGS", data.data);
      })
      .catch((error) => {
        Bugsnag.notify(error);
      });
    },
    // fetch and store data about the listing user has navigated to
    storeCurrentSelectedListing(state, payload) {
    const endPoint = process.env.VUE_APP_API_URL + "/internalapi/object/bid-data/id/" + payload.listingId;

    fetch(endPoint, {
      credentials: 'include'
    })
    .then(response => {
      window.storeValidateSession(response);
      return response.json()
    })
    .then((data) => {
      state.commit("STORE_CURRENT_LISTING_WITH_ACTIVE_BIDDINGS", data.data);
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
    },
    storeCurrentUserMessagingTemplates(state) {
      if (this.state.currentUserMessagingTemplates === null) {
        const endPoint = process.env.VUE_APP_API_URL + "/internalapi/message-template/get-all";

        fetch(endPoint, {
          credentials: 'include'
        })
        .then(response => {
          window.storeValidateSession(response);
          return response.json();
        })
        .then((data) => {
          state.commit("STORE_CURRENT_USER_MESSAGING_TEMPLATES", data.data);
        })
        .catch((error) => {
          Bugsnag.notify(error);
        });
      }
    },
    /**
     * Called when we create a new bidder with a new bid. Provides
     * data for the send message modal
     * @param state
     * @param payload
     */
    storeContactDataAfterNewBidder(state, payload) {
      let receiverData = {};


      fetch(process.env.VUE_APP_API_URL + "/internalapi/bid/get-receivers/objectId/" + payload.objectId, {
        credentials: 'include'
      })
      .then((response) => {
        window.storeValidateSession(response);
        if (response.status === 500) {
          alert("Någonting gick fel. Kontakta supporten om problemet kvarstår.");
          return false;
        }
        return response.json()
      })
      .then((data) => {
        if (data.errorMessage !== null) {
          if (data.errorMessagePretty) {
            alert(data.errorMessagePretty);
          } else {
            alert("Oväntat fel. Vänligen försök igen.");
          }
          return false;
        }

        //this.messageReceivers = data.data.bidders;
        receiverData.receivers = state.getters.notifyThesePeople;

        /**
         * since new bid has been made, we also need to get the users default template
         * suject and content for when new bids are made
         */
        fetch(process.env.VUE_APP_API_URL + '/internalapi/message-template/get-bid-standard/objectId/'+ payload.objectId, {
          credentials: 'include'
        })
        .then((response) => {
          window.storeValidateSession(response);
          if (response.status === 500) {
            alert("Någonting gick fel. Kontakta supporten om problemet kvarstår.");
            return false;
          }
          return response.json()
        })
        .then((data) => {
          if (data.errorMessage !== null) {
            if (data.errorMessagePretty) {
              alert(data.errorMessagePretty);
            } else {
              alert("Oväntat fel. Vänligen försök igen.");
            }
            return false;
          }
          receiverData.chosenMessagingTemplate = data.data.id;
          receiverData.message = data.data.content;
          receiverData.subject = data.data.subject;
          receiverData.deliveryWay = "sms";
          state.commit("STORE_SEND_SMS_OR_MAIL_CONTACT_DATA", receiverData);
        })
        .catch((error) => {
          Bugsnag.notify(error);
        });
      });
    },
    // Store bid receivers
    storeBidReceivers(state, payload) {
      fetch(process.env.VUE_APP_API_URL + "/internalapi/bid/get-receivers/objectId/" + payload.id, {
        credentials: 'include'
      })
      .then((response) => {
        window.storeValidateSession(response);
        if (response.status === 500) {
          alert("Någonting gick fel. Kontakta supporten om problemet kvarstår.");
          return false;
        }
        return response.json()
      })
      .then((data) => {
        if (data.errorMessage !== null) {
          if (data.errorMessagePretty) {
            alert(data.errorMessagePretty);
          } else {
            alert("Oväntat fel. Vänligen försök igen.");
          }
          return false;
        }
        state.commit("SET_CURRENT_LISTING_WITH_ACTIVE_BIDDINGS_BID_RECIEVERS", data.data);
      })
      .catch((error) => {
        Bugsnag.notify(error);
      });
    }
  },
  modules: {
    showing
  },

  /**
   * Think of getters as computed properties but for store.
   * For example a getter that gets only listings with more than X bids
   */
  getters: {
    getListingsWithActiveBiddings(state) {
      return state.listingsWithActiveBiddings;
    },
    getCurrentActiveListing(state) {
      return state.currentListingWithBiddings;
    },
    getAllRecentListings(state) {
      return state.allRecentListings;
    },
    /**
     * This getter will return all stakeholders except for excluded ones.
     * @param state
     * @returns {*[]}
     */
    getAllStakeHolders(state) {
      let stakeholdersTemp = [];

      Object.entries(state.currentListingWithBiddingsBidReceivers).forEach((receiver) => {
        if (receiver[0] !== 'exceptions') {
          if (receiver[1]) {
            const receiversTemp = receiver[1];
            if (Array.isArray(receiversTemp) && receiversTemp.length > 0) {
              receiversTemp.forEach((receiver) => {
                if (receiver.subcustomers) {
                  if (receiver.firstname && receiver.lastname) {
                    stakeholdersTemp.push(receiver);
                  }
                  receiver.subcustomers.forEach((subCustomer) => {
                    stakeholdersTemp.push(subCustomer);
                  });
                } else if (receiver.stakeholders && receiver.stakeholders.length > 0) {
                  const stakeholders = receiver.stakeholders;
                  stakeholders.forEach((stakeholder) => {
                    stakeholdersTemp.push(stakeholder.customer);
                  });
                } else {
                  if (receiver.firstname && receiver.lastname) {
                    stakeholdersTemp.push(receiver);
                  }
                }
              });
            } else {
              stakeholdersTemp.push(receiversTemp);
            }
          }
        }
      })

      // Filter out duplicated in bid recievers.
      const receiversFiltered = stakeholdersTemp.filter((item, index, arr) => {
        return arr.map(mapObj => mapObj.id).indexOf(item.id) === index;
      });
      state.receivers = receiversFiltered;
      return state.receivers;
    },
    getStakeholders(state) {
      let stakeHolderGroups = state.currentListingWithBiddings.stakeholderGroups;

      let arr = [];

      stakeHolderGroups.forEach((i) => {
        // make sure there is stakeholders to be added and not deleted/inactivated.
        if (!i.stakeholders.length || i.deleted) {
          return false;
        }
        let group = {
          stakeHolderGroupCustomersIds: [''],
          persons : ""
        };

        // Get the id's of every user ID in the stakeholder group (lodash)
        group.stakeHolderGroupCustomersIds = _.map(i['stakeholders'], 'customer.id');

        /**
         * then make a string of their names like 'John Doe, Bosse Bosseson'
         * etc for the dropdown.
         */
        let len = i.stakeholders.length;
        i.stakeholders.forEach((y, index) => {
          group.persons += y.customer.firstname + ' ' + y.customer.lastname;
          if (index < len - 1) {
            group.persons += ", ";
          }
        });
        arr.push(group);
      });

      return arr;
    },
    notifyThesePeople(state, getters) {
      let notifyThesePeople = [];

      if (getters.getAllStakeHolders.length > 0) {
        getters.getAllStakeHolders.forEach((receiver) => {
          // there are no reciever exceptions, add reciever immediately
          if (!state.currentListingWithBiddingsBidReceivers.exceptions) {
            notifyThesePeople.push(receiver);
          }
          // add reciever unless id in exceptions
          else if (state.currentListingWithBiddingsBidReceivers.exceptions
              && state.currentListingWithBiddingsBidReceivers.exceptions.map(function(item) { return item.customerId; }).indexOf(receiver.id) === -1) {
            notifyThesePeople.push(receiver);
          }
        })
      }

      if(!notifyThesePeople.length) {
        Bugsnag.notify("Notice:: Empty notifyThesePeople. ObjectId: " + state.currentListingWithBiddings.id);
        Bugsnag.notify(state);
      }

      return notifyThesePeople;
    },

    /**
     * Retrieves all sellers and any extra contact persons.
     */
    getSellersAndExtraContactPersons(state) {
      let sellers = {};
      if (state.sendSmsOrMailWhichService === 'showing') {
        sellers = state.showing.currentListingWithActiveShowing.sellers;
      } else if (state.sendSmsOrMailWhichService === 'bidding') {
        sellers = state.currentListingWithBiddings.sellers;
      }

      let sellersAndExtraContacts = [];
      sellers.forEach((seller) => {
        sellersAndExtraContacts.push(seller);
        if (seller.subcustomers && seller.subcustomers.length) {
          seller.subcustomers.forEach((subcustomer) => {
            sellersAndExtraContacts.push(subcustomer);
          });
        }
      });
      return sellersAndExtraContacts;
    }
  }
})
